import { asset } from './Brand';

/**
 * Support link.
 *
 * The URL lives here as a single named constant so changing the handle is a
 * one-line edit rather than a search across components.
 *
 * Note this must be the PUBLIC page (buymeacoffee.com/<handle>), not the
 * studio.buymeacoffee.com payout dashboard — that one is login-gated and would
 * send every visitor to a sign-in screen for an account they do not have.
 */
export const SUPPORT_URL = 'https://buymeacoffee.com/hazelled';

/**
 * Buy Me a Coffee's own approved button, from the brand kit at
 * buymeacoffee.com/brand, byte-for-byte as they ship it. Their guidelines ask
 * that the button not be redrawn or restyled, so it carries no border, no
 * background and no hover recolouring of its own — a pill drawn around
 * somebody else's button is exactly the alteration they are asking people not
 * to make.
 *
 * SERVED FROM THIS ORIGIN, not from their CDN. A hotlinked image would be a
 * request to a third party on every page load, which hands them the IP address
 * of every visitor and would make the site's own notice — "no tracking, no
 * advertising, no third-party scripts" — true only on a technicality. A copy
 * in public/ costs 37 KB once and tells nobody anything.
 *
 * Deliberately still a link and still an aside, sitting beside the account
 * controls: everything in Hazelled is free, and the official button is already
 * as loud as this should get.
 *
 * `disabled` mirrors the other header controls: during a test they are all
 * withheld, because navigating away mid-attempt loses the learner's place. A
 * link cannot be disabled, so it is removed from the flow entirely instead of
 * being left clickable-but-styled-dead.
 */
export function SupportLink({ disabled = false }: { disabled?: boolean }) {
  if (disabled) return null;
  return (
    <a
      className="support-link"
      href={SUPPORT_URL}
      target="_blank"
      rel="noopener noreferrer"
      /*
       * The name is on the link, and the image is marked decorative, so it is
       * announced once. Giving the img alt text as well would produce "Buy me
       * a coffee, Buy me a coffee — support this work".
       *
       * It begins with the words visible in the button, so a speech-input user
       * can say what they see (WCAG 2.5.3 Label in Name).
       */
      aria-label="Buy me a coffee — support this work (opens in a new tab)"
    >
      {/* Intrinsic size 545x153; width and height are set so the row does not
          jump when the SVG arrives. */}
      <img className="support-art" src={asset('bmc-button.svg')} alt="" width={121} height={34} />
    </a>
  );
}
